// Global Instructions Rule Applied!
import { buildFullUrl, getBackendBaseUrl } from '../utils/endpoints.ts'
import { API_ROUTES } from '../utils/endpoints.ts'
import { filenameFromContentDisposition } from '../utils/contentDisposition.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface JsonToExcelParams {
  data: Record<string, unknown>[]
  columns?: string[]
  filename?: string
  /** Column headers whose values should be written as Excel date cells (not plain text). */
  dateColumns?: string[]
  /**
   * Column headers for Excel Percentage format. Values must be fractional rates (e.g. 0.09 for 9%).
   */
  percentageColumns?: string[]
}

/** A column in a branded export: where to read it, its heading, its format. */
export interface BrandedExcelColumn {
  key: string
  title: string
  type: 'text' | 'currency' | 'ratio' | 'percent' | 'date'
}

/** The masthead of a branded export — what the report is, as of when, over what. */
export interface BrandedExcelMeta {
  report_title: string
  as_of_label?: string
  subtitle?: string
  footer_note?: string
}

export interface BrandedJsonToExcelParams {
  data: Record<string, unknown>[]
  columns: BrandedExcelColumn[]
  meta: BrandedExcelMeta
  /** Positions in `data` that are total rows — the backend never re-derives them. */
  totalRowIndexes?: number[]
  filename?: string
}

/** POST a JSON body, then save the returned workbook under the server's filename. */
async function postAndSaveWorkbook(
  url: string,
  body: unknown,
  accessToken: string | null,
  fallbackFilename: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` })
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      return { success: false, error: (err as { message?: string }).message || response.statusText }
    }

    const blob = await response.blob()
    const filename = filenameFromContentDisposition(
      response.headers.get('Content-Disposition'),
      fallbackFilename
    )

    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(objectUrl)

    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Export failed' }
  }
}

export async function exportJsonToExcel(
  params: JsonToExcelParams,
  accessToken: string | null
): Promise<{ success: boolean; error?: string }> {
  return postAndSaveWorkbook(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.UTILITIES.JSON_TO_EXCEL),
    {
      data: params.data,
      columns: params.columns,
      filename: params.filename,
      ...(params.dateColumns?.length ? { dateColumns: params.dateColumns } : {}),
      ...(params.percentageColumns?.length ? { percentageColumns: params.percentageColumns } : {})
    },
    accessToken,
    params.filename || 'export.xlsx'
  )
}

/**
 * Export in the firm's report design system — masthead, ink rules and the
 * accounting number conventions — rather than the plain grid dump.
 */
export async function exportJsonToExcelBranded(
  params: BrandedJsonToExcelParams,
  accessToken: string | null
): Promise<{ success: boolean; error?: string }> {
  return postAndSaveWorkbook(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.UTILITIES.JSON_TO_EXCEL_BRANDED),
    {
      data: params.data,
      columns: params.columns,
      meta: params.meta,
      ...(params.totalRowIndexes?.length ? { totalRowIndexes: params.totalRowIndexes } : {}),
      filename: params.filename
    },
    accessToken,
    params.filename || 'export.xlsx'
  )
}

export interface GlobalSearchResult {
  entity_type: string
  entity_id: string
  title: string
  subtitle: string
  metadata: Record<string, unknown>
}

export interface GlobalSearchResponse {
  success: boolean
  data?: {
    funds: GlobalSearchResult[]
    consolidated_funds: GlobalSearchResult[]
    accounts: GlobalSearchResult[]
    investors: GlobalSearchResult[]
    deals: GlobalSearchResult[]
    contacts: GlobalSearchResult[]
  }
  error?: string
}

export async function searchGlobal(
  query: string,
  accessToken: string | null
): Promise<GlobalSearchResponse> {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.UTILITIES.GLOBAL_SEARCH, {}, { q: query })
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` })
      }
    })

    const json = (await response.json()) as GlobalSearchResponse
    if (!response.ok) {
      return { success: false, error: json.error || response.statusText }
    }
    return json
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Search failed' }
  }
}
